import { AssetId, AssetIdError } from "./asset-id";
import { CameraId, CameraIdError } from "./camera-id";

declare const AssetCameraKeyBrand: unique symbol;

/**
 * A unique identifier for a Camera within an Asset.
 *
 * Structure: {asset}@{camera}
 * Example: A2-G2-CAM_11144@camera-a
 */
export type AssetCameraKey = string & { readonly [AssetCameraKeyBrand]: "AssetCameraKey" };

export class AssetCameraKeyError extends Error {
  constructor(
    public readonly kind: "Format" | "InvalidAsset" | "InvalidCamera",
    message: string,
    options?: ErrorOptions,
  ) {
    super(message, options);
  }
}

/**
 * Constructs an AssetCameraKey from its component parts.
 *
 * @param asset - The AssetId
 * @param camera - The CameraId
 */
const fromParts = (asset: AssetId, camera: CameraId): AssetCameraKey => {
  return `${AssetId.toString(asset)}@${CameraId.toString(camera)}` as AssetCameraKey;
};

/**
 * Canonical encoding is '{asset}@{camera}'.
 */
const toString = (key: AssetCameraKey): string => {
  return key as string;
};

/**
 * Parses an AssetCameraKey from canonical encoding or throws an AssetCameraKeyError.
 * (Accepts format: {asset}@{camera})
 */
const fromString = (str: string): AssetCameraKey => {
  const [assetString, cameraString, ...rest] = str.split("@");
  if (assetString === undefined || cameraString === undefined || rest.length > 0) {
    throw new AssetCameraKeyError(
      "Format",
      `String ${str} is not a valid AssetCameraKey. Expected format: {asset}@{camera} with exactly one '@' separator.`,
    );
  }

  try {
    const asset = AssetId.fromString(assetString);
    const camera = CameraId.fromString(cameraString);
    return fromParts(asset, camera);
  } catch (error) {
    if (error instanceof AssetIdError) {
      throw new AssetCameraKeyError("InvalidAsset", `String ${str} has an invalid Asset ID.`, { cause: error });
    }
    if (error instanceof CameraIdError) {
      throw new AssetCameraKeyError("InvalidCamera", `String ${str} has an invalid Camera ID.`, { cause: error });
    }
    throw error;
  }
};

/**
 * Extracts the component parts from an AssetCameraKey.
 *
 * @param key - The AssetCameraKey to decompose
 * @returns An object with asset and camera properties
 */
const toParts = (key: AssetCameraKey): { asset: AssetId; camera: CameraId } => {
  const str = key as string;
  const [assetString, cameraString, ...rest] = str.split("@");

  if (assetString === undefined || cameraString === undefined || rest.length > 0) {
    throw new AssetCameraKeyError(
      "Format",
      `AssetCameraKey ${str} has invalid format. Expected exactly one '@' separator.`,
    );
  }

  try {
    const asset = AssetId.fromString(assetString);
    const camera = CameraId.fromString(cameraString);
    return { asset, camera };
  } catch (error) {
    if (error instanceof AssetIdError) {
      throw new AssetCameraKeyError("InvalidAsset", `AssetCameraKey ${str} has an invalid Asset ID.`, {
        cause: error,
      });
    }
    if (error instanceof CameraIdError) {
      throw new AssetCameraKeyError("InvalidCamera", `AssetCameraKey ${str} has an invalid Camera ID.`, {
        cause: error,
      });
    }
    throw error;
  }
};

const asset = (key: AssetCameraKey) => toParts(key).asset;
const camera = (key: AssetCameraKey) => toParts(key).camera;

export const AssetCameraKey = {
  fromParts,
  toString,
  fromString,
  asset,
  camera,
} as const;
